import { Box } from "@mui/system";
import React from "react";
import { getExternalUrl, logLinkClick } from "../../utils/links";
import { Button, Link, Stack } from "@mui/material";
import { HEADING_FONT } from "../../constants/colors";

const RETAILER_DISPLAY_NAMES = {
  rei: "REI",
  amazon: "Amazon",
  campsaver: "CampSaver",
  backcountry: "Backcountry",
  moosejaw: "Moosejaw",
  hyperlite: "Hyperlite Mountain Gear",
  kelty: "Kelty",
  garagegrowngear: "Garage Grown Gear",
  sixmoondesigns: "Six Moon Designs",
  zpacks: "Zpacks",
  featheredfriends: "Feathered Friends",
  outdoorvitals: "Outdoor Vitals",
  blackdiamondequipment: "Black Diamond",
  enlightenedequipment: "Enlightened Equipment",
  mountainsmith: "Mountainsmith",
  decathlon: "Decathlon",
  msr: "MSR",
  gossamergear: "Gossamer Gear",
  thermarest: "Therm-a-Rest",
  "therm-a-rest": "Therm-a-Rest",
  nemo: "NEMO",
  seatosummit: "Sea to Summit",
  "sea to summit": "Sea to Summit",
  bigagnes: "Big Agnes",
  "big agnes": "Big Agnes",
  exped: "EXPED",
  klymit: "Klymit",
};

const getRetailerDisplayName = (retailer) =>
  RETAILER_DISPLAY_NAMES[(retailer || "").toLowerCase()] || retailer;

// Re-draws the logo on a canvas and reports whether enough of its pixels
// contrast with the current background to be legible (an all-white logo on a
// light card renders invisibly, yet never fires onError).
const logoIsInvisible = (img, darkMode) => {
  try {
    const width = Math.min(img.naturalWidth, 96);
    const height = Math.max(
      1,
      Math.round((img.naturalHeight * width) / img.naturalWidth)
    );
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, width, height);
    const data = ctx.getImageData(0, 0, width, height).data;
    const total = data.length / 4;
    let visible = 0;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 40) continue;
      const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      if (darkMode ? lum >= 90 : lum <= 200) visible++;
    }
    return visible / total < 0.03;
  } catch (e) {
    // CORS-tainted canvas or decode quirk — can't verify, keep the image.
    return false;
  }
};

// Renders a retailer logo, falling back to the retailer's name as text when
// the logo URL is missing, fails to load (many are hotlinked and die), or
// loads but has no contrast against the current background.
const BrandLogo = ({ retailer, src, imgSx, textSx, darkMode }) => {
  const [failedSrc, setFailedSrc] = React.useState(null);
  const [invisibleSrc, setInvisibleSrc] = React.useState(null);

  // Probe with a separate CORS-enabled image so the visible one is never
  // affected; hosts without CORS headers simply skip the check.
  React.useEffect(() => {
    if (!src) return undefined;
    let cancelled = false;
    const probe = new Image();
    probe.crossOrigin = "anonymous";
    probe.onload = () => {
      if (!cancelled && logoIsInvisible(probe, darkMode)) {
        setInvisibleSrc(src);
      }
    };
    probe.src = src;
    return () => {
      cancelled = true;
    };
  }, [src, darkMode]);

  if (!src || failedSrc === src || invisibleSrc === src) {
    return (
      <Box
        component="span"
        sx={{
          fontFamily: HEADING_FONT,
          fontWeight: 700,
          fontSize: "14px",
          letterSpacing: "-0.01em",
          whiteSpace: "nowrap",
          ...textSx,
        }}
      >
        {getRetailerDisplayName(retailer)}
      </Box>
    );
  }

  return (
    <Box
      component="img"
      alt={retailer}
      src={src}
      onError={() => setFailedSrc(src)}
      sx={imgSx}
    />
  );
};

const nemo = "/nemo.png";

const seaToSummitLight =
  "https://tenereteam.s3-us-west-1.amazonaws.com/sea-to-summit-usa-logo-9?v=1734699078";
const seaToSummitDark =
  "https://liveferal.com/wp-content/uploads/2018/04/Sea-to-Summit-min.png";

const REIWhite = "/REI-white.svg";

const REI =
  "https://ncbaclusa.coop/content/uploads/2018/10/rei-coop-500_4fb8b.jpg";

const exped = "/exped.jpg";

const bigagnes = "/bigagnes.jpg";

const amazonWhite = "/amazonfull.png";
const amazon = "https://i.imgur.com/cNWpW7D.png";

const campsaverWhite = "/campsaver.png";
const campsaver =
  "https://sotooutdoors.com/wp-content/uploads/2020/10/campsaver-logo-2020.png";

const garagegrowngear = "https://i.imgur.com/EzTgtfP.jpg";

const garagegrowngearWhite =
  "https://cdn.shopify.com/s/files/1/0537/1177/files/ggg-logo_3x_9074e10b-c943-4bdc-9c87-c8be58c7c9e6_230x.png?v=1679436198";

const backcountry =
  "https://logos-download.com/wp-content/uploads/2019/01/Backcountry_Logo_full.png";

const thermarest =
  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQz0Cmg7VkYsNnOubh4zMzkvIcuK1pBPFBKstzEVG7a&s";

const thermarestWhite =
  "https://www.themountainfactor.com/wp-content/uploads/2022/11/thermarest-logo-white.png";

const klymit =
  "https://cdn.shopify.com/s/files/1/0084/4854/0762/files/Klymit_Logo_White_Horizontal_1_410x.png?v=1636402252";

const moosejaw = "https://i.imgur.com/SR6Lr2w.png";

const hyperlite =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAY0AAAB/CAMAAAAkVG5FAAAAflBMVEX///8AAAC9vb329vbq6upYWFg2NjbAwMCRkZFVVVXX19fGxsZMTExbW1ujo6NpaWmwsLARERGYmJjk5OSpqanMzMz4+PiKiopkZGR0dHRvb28rKytFRUXQ0NB+fn7o6Og8PDwdHR0wMDB5eXkWFhYlJSU6OjqVlZWEhIRJSUnNKv+mAAAMuklEQVR4nO1dbWOyOgwV0SmKioKgos7Xzf3/P3iRptqkQYpT2X3s+TTlpbWnbdKTtGs0LCwsLCwsXoVoUncNLC5wHadVdx0sJBaO49VdBwvA1MnQrLsWFgKHMxtO3bWwyHHKyXAWddfDIkPPAazqrolFo7GRbBzqrolFY+hc0K27Lm+PlqMgrLs2746Bysay7tq8OZoOQlR3fd4a7liwsE+ADrfuGr0z9hcSgJZZ3TV6Y0yBjKDR6MOfnbrr9L74EAzkiuFM/D2O667Uu6IL4yFX0134sK67Vm8KKYkMxccAPvbqrdW7whet/yU//4jPH3XW6W0R0bEgl+U2Kvt6yLZPrl9NrEBSF0ai5Y+qD7VTfCyLF6ID46CvfintelBXrd4UcSranQT81lYgqQOngmYfsyRZPBWroilJCiR97imL52Ap2rytXwHjnlqB5GW44cpKgSTRL1k8BeGtZZ4VSF4MiL5u+KsgmNio7GtQ0vvlyBm+tlZvitZWtPap6AZpVT5fWat3xRwkkeIVHgShbFT2+TAIuUqBxKatPxvxh8Fq+1Q6fCweAkjVGd/cyxSDadm/qlZvCkOVViq809fU6l0Bi4nSCMZC3PdlBZInQiakl0b3ZGTw+xW1elOE4xuSCIYVSJ4OyGDbmMw/pnOaxZ2QCelGW8qkQGLT1p8DNy2RRDBAIDnYff1Pwbrimu5L3D96aqXeFTIh3VjvkOFam7b+eMSbyl0dBJLUCiQPx7eJJILhbisZGuXBTj/DdSHfaWboXzy5z/yykgjh5p/7Vwlf3HAFFvdbTQ1qVkWsXe2Q9ZUojxUaevkDip/T1wsLyd0IRvKFlESwi6Q5TDjEJAWSqvv6h3iNuSJLlxVVwXxygyz3CrSPYaRdRudy9JnLuEPBYorrmCJ/Y3z5HDMvuyqu3u2aFAKe89GXU5/etsTUwu+uGpUd4tbtk8aWuotkCyzaNZ6is4HouIMNvBf+Bht+JTYGzFWD9uGX1qm2tvPwgQp3pq2XsRHiX/VDG4dhQ635PWwgMahmNlogiWDZqasnVHkkdUduK6iWtl7GhtT1xVthaCiTCceGojrfxYY66Gtmg5VkQya9zaMND1P6oLwMBaVsgH8gfhYMDaVuHBuKifufs8GHK3yejQ36QgokldLWS9mQC/0z8zA0VP8B6juJhmd88WyMR4vRBWpcDIqDq4vZr9iQSQIqrjOMYGM7UlB6WItMSMcbLM+TEMcGSd0Bz3hbZdFRzkYstoqch5wYGsheCTbGUOSAZ6MwObWP+2j8OzYElN6jYnD5FeaAWRpLIrmBZtnAtZSB9HmFAsvZkApmT44DpBB0UGu1C9go6h+EDfcRbAj7OX4AG3wKyKyYDfx2KZBUSFsn640pwwY49gM4Hgv70P8yG5CQjtOjRN/k2SC8gdr4YR6VBTaWa4Evjg2gCMYtXl+asRG2JHDNXs6Gf6lIudAho69IXYCskAI2HPTzpEBifpiYcgKZAhJGVP0RkkhnxIYK1EovZ0NFiXn9hNuwbYbjXorYwEZCRqmMo7JGbITKJZJkClYc+kSBFS98NbDhQncNX8pGycIMHvhBX0pbUMQGmTlm3DtuwIiNywFAuixZeb3BsUHwJ9jg+7V0ogvZOKJvpUBiGpU1Y+N6iB8d3v8qG/I4MDznf8tnC9kgD0iBxFCON2Pjkgyv7U3g2FBiXmZ2g0Ad7XXZDZgNPqgkUsoGL5AYpq0DGzPwqbwCNqR7oD3PsKHGZcx8Kgy0VnwwG56hTyWjr3it4JuwgVcA1dLWTdYb14roFDNsqMqM2XoDAxnCetYboD8QFym61vEGG2T+gLOszAQSg7V4DmM2vpDCZsbG4LQ+SUdhhpuxHjbYJpTqehkbvEBidJjYg9gIXH78m+lU+XwAi0sSnwE2uM1bz2OD119VG3iTDdxKctIzico+iI0iLQY03O5EgXIZisvNvtxwzY6N7QbwcdXI72Fjp1akcLskG5tAJ+DeZIMYCRj1JmmjL2EDQ7mssnFxKNALAvr0jlTpfp+qML7Bxu3cI2GjybwQcHC5Jw2isn+IjcaReZfGxvW8umexIRdXeOis0ZO32SB7m+Sd5VHZv8QG1PpLfUENbICcgT3VFX6yhA0SLIQ3lqet/wk25DwLk6/aKV/PhvQScRts8JNlbOATJ6UAWRqVNV1vyBhHQd2LEsVmRmxILuVSSfHMNDbSy6WfW2xo04JpPpXcSIlTQL7Jk2VsEIEEWrk0X1EkA160DEj+09cHIlVPZ8kVDxT5C6uuBrXnx+TplfisVFpLVrzyruUaqk9og3Wq14TtqpBDuyuQREzZIB0avDR7mFg18Pnl2sDK2Phsdq7ob+gdOBtRjnp7mFglfHGdOKJNXbLeyIFVdFjb2rT1KmD3JbUO97AxZgUSe5iYOXhJZKG1tAkbZMOHFEjtXlljeFxbc+bahA0ikMARPvYwMVNIdxpLIum9bJCsOJAa7GFiZpC6JRaUTkw7m7FBVHSW60r1+6suQPyMmoF92KAvV1wzG7JBFsUgB9yKys73wGAyp/a+P9o6/pAs7CaQNTLCrvNpns+S7ky1UsFiPt/v5/MFtoruXAu9NOeodZsLUZcJr3uG693hOCL2cJ4VlBWlJVjMtSM/gvON+8IDhtF6Ml7+ho0Nu4a8EZWVxX9q/85j4nhBf+94mA4PpAky/6UiM7qFukPke23n0PZ8fG+kexZdnDMwAXfdY6NDK2ecDL/H5CXOuO15nk/fPNSnhsTZ7/c+7aNSEsE5SlQSqcYG2YUDAsmheFw7C1H+JF1gNlbCN+4QyaUNdxE2dstcRm5pipWv52iPEy0t65uyscld/gE3quPDQRCPqeK3EMe7xZEW1s0FqhOJxsn1Gep8Pb6RdTm2gA1eICk+TMzpzvItAB9JgtlYAIUejg4XsJEuhueSGTY0babvuHuHzH8aG/3cBLJsBDDUQxye5efjvtPa01wdwUYHqx+y3bEkMk8/OOy0HzXasTem+EZZSOFuXKfbPF8Mnd4JsRF/wa+b4Kh7ERujxvHHjA3vJxt3JPtOY6M1OXddlo0Br4RnbMQZyLftQVYYMRHdrC/E8QIXye/RdwugqaRx0Z34NhiAuyKVNZuPziMnOTbWiI2WTF8Z4hFdzEYn+9UGbITnrr3d4S81Nj7j1C9gY3fOrgy/J8TEO4flcrnx8c/vZYXFH2Q/cdfJ7kxxTU01719CCiRFh4llbCyyzradFLERmbLRmDmuARuJk78UT6gaG2FGbsSzkZ5n1s52R+ITzkdyOp0S3OtO53/oMySFdZ3svvZYdQqrJszejRKBJGMjm6p62c8nbGzBSCZkpoLwDsNG6KzdUjZiZ5v5lwOikjJsNGbjmGXDy42O2/ghbDBW3N0eM793RsymsBs/W/VXiTYyTia/H7Co0bb/C2RsxM68u2kQNrLpWVjJJZ7kFqINWmQyPrORtWmzlI0g33O52GA7zrEROsnslhX3CRvMvZEzOxe2G6O3CzYi5XEpRaXdJ5+63Rpubo7Csx+7dg6BxkYgelREBkFTeLxr4qjkbMRpWsrGTvS/KX4tx0b25Zj1cI/bfLRSNpixsRSeaB//dsFG87oOkZLIGfMniqzhSdHmWQt1dv1X+bV9iq8ssoV4MNJ+5Nrxk9OSLii3ebt1dOdtiZ+fygfx1lpt9XduKXfHe629o7MPohO1G4dBBl81crKweIdyzXPLlV28jG4sRQ36zzk+dYqTBNiobK5a5OpBRFdJgZemvj6iOqOlrwkQJ9HVu1SvaCR4aAWSnGCmdo7mHLVAZ55fnI54ZcSNZu327BsXNZ8vMqDyo73LFRbkHLujCxst2OgusYweroK5wQ8qYjepPif+WdXw0YibeDF9fKwBaU126PU+kwdioaJHQnyPMyC9/Ri9eVT12Kq3xGeyRa3mPeRgwj7Oqzuc7H+TNYQb4UyczW8NiBthRX4ztOeyVkBMuvIx+UVXDrsosd1pd+xh91XR26MmdBZ3TvPPM0TvhdY37tP3GJAOcdK+7RR1N9wAz/df1QyIO8RnZS0D69H+DmTpvDU3ICHxzWb2P9U8ACHez2S4TliRXRLWo30UWhOc2uaXbsJv4hkuHdop6oGIm0RfutW8rSElz3q0j8YKb5c7FBmQ3hoLIJqyavEQhEm5AZm28T2J/W+xT4NL5qAlMSAB9mg/IjtFPRdNHzX48WpAWhO83WZg95O9AL05mYxC7tu99WhfhFYXt/ys18cjZjyxHu0LEUc4ioewqXRousUj0C/IgJ7ZkF4tCPc6Fyfr0dYGF+/rGNM9RxYvRnAxIHTxYVEHpnnQ1gogfwXhPrEerYWFhYWFRV34Dwh6sXqlXw7MAAAAAElFTkSuQmCC";

const hyperliteWhite =
  "https://davidlottmann.files.wordpress.com/2015/11/hmg_logo_white_020116.png";

const kelty =
  "https://upload.wikimedia.org/wikipedia/en/thumb/3/31/Kelty_logo.svg/220px-Kelty_logo.svg.png";

const backCountryWhite =
  "https://content.backcountry.com/promo_upload/bcs/2021/OB21_0045/Gore_logo2.png";

const zpacks =
  "https://zpacks.com/cdn/shop/files/zpacks_crest_only_dark_blue_c0fa6183-640b-4f88-9a11-11a4aa270c89_1200x1200.png?v=1613649619";

const featheredFriendsBlack =
  "https://storage.googleapis.com/pack_wizard/static_images/featheredfriendsblack.png";

const featheredFriendsWhite =
  "https://storage.googleapis.com/pack_wizard/static_images/featheredfriendswhite.png";

const sixmoondesignswhite = "/smd_white.png";

const sixmoondesignsblack = "/smd_black.png";

const outdoorvitalswhite =
  "https://tribe.outdoorvitals.com/hosted/images/bb/d1b5e13a6148f1839bbceeb7e91fe7/Secondary-Logo-White-with-clear---tight-crop.png";

const outdoorvitalsblack =
  "https://www.pctdays.com/wp-content/uploads/2023/05/OutdoorVitals2.png";

const bdwhite =
  "https://static.wixstatic.com/media/d0fe0b_d0ab9f6e0c9944fdb08669c21ef29e1d~mv2.png/v1/fill/w_315,h_142,al_c,q_85,usm_0.66_1.00_0.01,enc_auto/BD%20Logo.png";

const bdblack =
  "https://d1io3yog0oux5.cloudfront.net/claruscorp/files/brand-logos/bd_logo.png";

const eewhite =
  "https://theme.zdassets.com/theme_assets/934091/86d7fe0d8cba8c5722ecb178447494cf653c1c78.jpg";
const eeblack =
  "https://cdn11.bigcommerce.com/s-japp2/stencil/d92525e0-5491-013c-5d9c-2ec360b8bf3e/e/441d5720-7ced-013c-bba9-2e40942744ad/img/common/ee-logo.svg";

const mountainsmith =
  "https://mountainsmith.com/cdn/shop/files/MS_Logo_PRIMARY_410x.jpg?v=1623189955";

const decathlon =
  "https://loghi-famosi.com/wp-content/uploads/2021/02/Decathlon-Logo.png";

const msrLight =
  "https://www.wta.org/our-work/about/corporate-sponsors/msrlogoblack.png/@@images/image.png";

const msrDark = "https://e-mot.co.jp/images/2022/04/MSR_logo_1.png";

const outdoorvitals =
  "https://cdn.shopify.com/s/files/1/1724/0065/files/logo_resized_1067d959-3352-49f8-b2d2-b39189b1b378.jpg?v=1626289028";

const gossamergear = "https://www.gossamergear.com/cdn/shop/products/3x3.png";

const BrandBuyButton = ({ retailer, url, darkMode, size, width, product }) => {
  const getBrandImageUrl = (retailer) => {
    if (retailer === "Therm-a-Rest" || retailer === "thermarest") {
      return darkMode ? thermarestWhite : thermarest;
    } else if (retailer === "NEMO" || retailer === "nemo") {
      return nemo;
    } else if (retailer === "Sea to Summit" || retailer === "seatosummit") {
      return darkMode ? seaToSummitDark : seaToSummitLight;
    } else if (retailer === "REI" || retailer === "rei") {
      return darkMode ? REIWhite : REI;
    } else if (retailer === "big agnes" || retailer === "bigagnes") {
      return bigagnes;
    } else if (retailer === "exped" || retailer === "exped") {
      return exped;
    } else if (retailer === "klymit" || retailer === "klymit") {
      // Only a white Klymit logo exists — invisible on light backgrounds, so
      // light mode falls back to the text name.
      return darkMode ? klymit : undefined;
    } else if (retailer === "amazon") {
      return darkMode ? amazonWhite : amazon;
    } else if (retailer === "campsaver") {
      return darkMode ? campsaverWhite : campsaver;
    } else if (retailer === "backcountry") {
      return darkMode ? backCountryWhite : backcountry;
    } else if (retailer === "moosejaw") {
      return moosejaw;
    } else if (retailer === "hyperlite") {
      return darkMode ? hyperliteWhite : hyperlite;
    } else if (retailer === "kelty") {
      return kelty;
    } else if (retailer === "garagegrowngear") {
      return darkMode ? garagegrowngearWhite : garagegrowngear;
    } else if (retailer === "sixmoondesigns") {
      return darkMode ? sixmoondesignswhite : sixmoondesignsblack;
    } else if (retailer === "zpacks") {
      // The Zpacks crest is dark blue — invisible on dark backgrounds, so
      // dark mode falls back to the text name.
      return darkMode ? undefined : zpacks;
    } else if (retailer === "featheredfriends") {
      return darkMode ? featheredFriendsWhite : featheredFriendsBlack;
    } else if (retailer === "outdoorvitals") {
      return outdoorvitals;
    } else if (retailer === "blackdiamondequipment") {
      return darkMode ? bdwhite : bdblack;
    } else if (retailer === "enlightenedequipment") {
      return darkMode ? eewhite : eeblack;
    } else if (retailer === "mountainsmith") {
      return mountainsmith;
    } else if (retailer === "decathlon") {
      return decathlon;
    } else if (retailer === "msr") {
      return darkMode ? msrDark : msrLight;
    } else if (retailer === "gossamergear") {
      return gossamergear;
    }
  };

  const sizeRules = {
    amazon: {
      darkMode: "35px",
      lightMode: "40px",
    },
    rei: {
      darkMode: "45px",
      lightMode: "50px",
    },
    kelty: {
      darkMode: "45px",
      lightMode: "45px",
    },
    backcountry: {
      darkMode: "45px",
      lightMode: "30px",
    },
    hyperlite: {
      darkMode: "45px",
      lightMode: "40px",
    },
    garagegrowngear: {
      darkMode: "30px",
      lightMode: "45px",
    },
    sixmoondesigns: {
      darkMode: "50px",
      lightMode: "50px",
    },
    seatosummit: {
      darkMode: "50px",
      lightMode: "50px",
    },
    gossamergear: {
      darkMode: "50px",
      lightMode: "50px",
    },
  };

  if (size === "small") {
    return (
      <Link
        target="_blank"
        href={getExternalUrl(url, false, product)}
        onClick={() => {
          logLinkClick(url);
        }}
      >
        <Button
          variant="contained"
          sx={{ width, height: "40px", backgroundColor: "#42a5f5" }}
        >
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ color: "white", fontSize: "12px" }}
          >
            View on
            <BrandLogo
              retailer={retailer}
              src={getBrandImageUrl(retailer, url)}
              darkMode={darkMode}
              imgSx={{
                height: "20px",
                paddingTop: "0px",
                paddingBottom: "0px",
              }}
              textSx={{ fontSize: "12px" }}
            />
          </Stack>
        </Button>
      </Link>
    );
  }

  return (
    <Link
      target="_blank"
      href={getExternalUrl(url, false, product)}
      onClick={() => {
        logLinkClick(url);
      }}
    >
      <BrandLogo
        retailer={retailer}
        src={getBrandImageUrl(retailer, url)}
        darkMode={darkMode}
        imgSx={{
          maxHeight:
            (sizeRules[retailer.toLowerCase()] || {})[
              darkMode ? "darkMode" : "lightMode"
            ] || "30px",
          paddingTop: "0px",
          paddingBottom: "0px",
          "&:hover": {
            opacity: "0.8",
            cursor: "pointer",
          },
        }}
        textSx={{
          "&:hover": {
            opacity: "0.8",
            cursor: "pointer",
          },
        }}
      />
    </Link>
  );
};

export default BrandBuyButton;
